import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { expandWeek, type WeekItem, type Coverage } from '@familyhub/shared';
import { adb, type ActivityRecord, type ChildcareRecord, type PersonRecord } from './db';

/**
 * The week, computed locally from the agenda mirror.
 *
 * Reads are from Dexie, never the network — so the week is identical with and
 * without signal, the same rule the groceries list follows. The heavy lifting
 * (recurrence expansion, coverage) is the shared `expandWeek`, run in a memo so
 * it recomputes only when the rows or the reference week change.
 */

export interface AgendaWeek {
  weekStart: string;
  days: string[];
  items: WeekItem[];
  coverage: Coverage;
  people: PersonRecord[];
  byId: {
    activity: Map<string, ActivityRecord>;
    childcare: Map<string, ChildcareRecord>;
  };
}

export function useAgendaWeek(refIso: string): AgendaWeek | undefined {
  const rows = useLiveQuery(async () => {
    const [activities, childcare, people] = await Promise.all([
      adb.activities.toArray(),
      adb.childcare.toArray(),
      adb.people.toArray(),
    ]);
    return { activities, childcare, people };
  }, []);

  return useMemo(() => {
    if (!rows) return undefined;
    const live = {
      activities: rows.activities.filter((r) => !r.deleted_at),
      childcare: rows.childcare.filter((r) => !r.deleted_at),
    };
    const { weekStart, days, items, coverage } = expandWeek(live as Parameters<typeof expandWeek>[0], refIso);
    return {
      weekStart,
      days,
      items,
      coverage,
      people: rows.people.filter((p) => !p.deleted_at),
      byId: {
        activity: new Map(rows.activities.map((r) => [r.id, r])),
        childcare: new Map(rows.childcare.map((r) => [r.id, r])),
      },
    };
  }, [rows, refIso]);
}
